import { groupToThirdPartyCalendarId } from 'components/calendar/CalendarView'
import { useEventPopoverCallbacks } from 'components/calendar/eventPopover/EventPopoverProvider'
import { AppUIType } from 'components/providers/AppUITypeProvider'
import { useCalendar } from 'components/providers/CalendarProvider'
import { useCalendarSync } from 'components/providers/CalendarSyncProdivder'
import { getDefaultCalendar } from 'components/providers/GetChangesProvider'
import dayjs from 'dayjs'
import { getDefaultTimezone, getMidnightUTC } from 'index'
import isEqual from 'lodash.isequal'
import ParticipantSuggestion from 'model/models/ParticipantSuggestion'
import { useEffect, useRef, useState } from 'react'
import {
  Control,
  useForm,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister
} from 'react-hook-form'
import Database from 'services/db/Database'
import { localEventById, localEventByLocalId } from 'services/db/Queries/Event'
import { localGroupById } from 'services/db/Queries/GroupQueries'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import { generateDefaultReminder } from 'services/db/utils/Event'
import { googleMapsService } from 'services/googleMapsService'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import {
  Attachment,
  Attendee,
  DocType,
  EventDoc,
  EventLocation,
  EventNote,
  EventParticipantStatus,
  EventRecurrence,
  EventReminder,
  EventStatus,
  GroupDoc,
  LocationReminder,
  SimpleLabel,
  TaskType
} from 'types/api/changes'
import { CollectionType, Model } from 'types/model'
import { LocalStorageKey } from 'types/services/localStorage'
import { isUserActiveInGroup } from 'utils/api/groups'
import { eventLengthInDays } from 'utils/api/instance_state'
import { isGroupHidden } from 'utils/api/userSettings'
import { is24meApp } from 'utils/appType'
import { APP_COLOR } from 'utils/colors'
import { isCypress } from 'utils/cypress'
import { generateStartEndBasedOnTime, isAMPM } from 'utils/date'
import { eventIdForContainer, isEventRecurrenceValid } from 'utils/event'
import { getFullCalendarEventId } from 'utils/fc_mapping'
import {
  groupToProvider,
  isConvertedGroupId,
  isEventHiddenInAllGroups,
  ProviderType
} from 'utils/groups'
import {
  appendNoteWithMetaData,
  clearNote,
  colorFromMetaData,
  META_DATA_LABEL,
  unPackMetaData
} from 'utils/note_meta_data'
import { isTrulyEmpty, removePrefix } from 'utils/object'

export interface IEventDetails {
  setStatus: (status: string) => void
  setLoading: (b: boolean) => void
  loading: boolean
  setOpenDiscard: (b: boolean) => void
  openDiscard: boolean
  setOpenAddReminder: (b: boolean) => void
  openAddReminder: boolean
  sendEventToServer(data: EventDoc): Promise<any>
  setStartTime: (date: number) => void
  startTime: string | undefined
  setEndTime: (date: number) => void
  endTime: string | undefined
  setTimeZone: (tz?: string | undefined | null) => void
  timeZone: string
  setRecurrence: (eventRecurrence: EventRecurrence, dirty?: boolean) => void
  reminders: EventReminder[] | undefined
  setReminders: (reminders: EventReminder[]) => void
  RSVP: string | undefined
  setRSVP: (state: string) => void
  notes: EventNote[] | undefined
  setNotes: (notes: EventNote[]) => void
  attendees: Attendee[] | undefined
  setAttendees: (attendees: Attendee[], shouldDirty?: boolean) => void
  attendeeText: string
  setAttendeeText: (t: string) => void
  addAttendee: (attendee: Attendee) => void
  noteText: string
  setNoteText: (t: string) => void
  addNote: (text: string, bytes?: string) => void
  updateNote: (note: EventNote) => void
  participantsStatus: Record<string, EventParticipantStatus> | undefined
  setParticipantStatus: (
    participantsStatus: Record<string, EventParticipantStatus>
  ) => void
  register: UseFormRegister<EventDoc>
  control: Control<EventDoc, any>
  isDirty: boolean
  handleSubmit: UseFormHandleSubmit<EventDoc>
  getValues: UseFormGetValues<EventDoc>
  setLocation: (loc: EventLocation | undefined) => void
  error: string
  setError: (errorText: string) => void
  setGroupID: (id: string, options?: SetGroupIDOptions) => void
  setSupplementaryGroupIds: (ids: string[]) => void
  supplementaryGroupIds: string[] | undefined
  setAllDay: (allDay: string) => void
  cacheEventData: () => void
  setName: (text: string) => void
  setThirdPartyId: (id: string) => void
  thirdPartyId?: string
  ownerId?: string
  setOwnerId: (id: string, options?: SetOwnerIDOptions) => void
  getCalendarMoveRequest: () => CalendarMoveRequest | undefined
  labels: SimpleLabel[] | undefined
  setLabels: (reminders: SimpleLabel[]) => void
  type: DocType
  setType: (type: DocType) => void
  taskType: TaskType
  setTaskType: (type: TaskType) => void
  someday?: string
  setSomeday: (s: string) => void
  createAtStart: number
  createAtEnd: number
  priority: string
  setPriority: (p: string) => void
  titleSet: boolean
  onTitleSet: (b: boolean) => void
  isNewEvent?: boolean
  groupId?: string
  dirtyFields: any

  setLocationPendingText: (text: string) => void
  locationPendingText: string
  setNotePendingText: (text: string) => void
  notePendingText: string
  setNotePendingBytes: (bytes: string | undefined) => void
  notePendingBytes: string | undefined
  addLocationToEvent: (text: string) => Promise<void>
  applyPendingData: () => Promise<void>
  attachements?: Attachment[]
  customUrl?: string
  setAttachments: (attachments: Attachment[] | undefined) => void
  setCustomUrl: (url: string | undefined) => void
  locationReminder: LocationReminder[]
  selectedColor: string | undefined
  setSelectedColor: (color: string) => void
  isLocalSaveOnly: () => Promise<boolean>
}

export interface SetGroupIDOptions {
  eventOverrides?: Partial<EventDoc>
  refreshCalendar?: boolean
  selectInSidebar?: boolean
  skipLocalWrite?: boolean
}

export interface SetOwnerIDOptions {
  refreshCalendar?: boolean
  writeLocal?: boolean
}

export interface CalendarMoveRequest {
  oldTaskId: string
  originalGroupId?: string
  originalThirdPartyId?: string
  originalOwnerId?: string
  nextGroupId?: string
  nextThirdPartyId?: string
  nextOwnerId?: string
}

export function useEventDetails(
  event: Model<EventDoc> | EventDoc,
  group: GroupDoc | undefined,
  createAtStart: number,
  createAtEnd: number,
  doNotScrollToEvent?: boolean,
  fcEventId?: string
) {
  let pendingData: EventDoc | undefined | null
  const storedData =
    LocalStorage.get(LocalStorageKey.PENDING_EVENT_DATA) ||
    LocalStorage.get(LocalStorageKey.DUPLICATE_EVENT_DATA)

  if (storedData) {
    pendingData = JSON.parse(storedData)
    LocalStorage.remove(LocalStorageKey.DUPLICATE_EVENT_DATA)
    Logger.debug('cached data', pendingData)
  }

  const newEvent = event?._id == undefined || event?._id?.length == 0

  const { pointTo } = useEventPopoverCallbacks()
  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    resetField,
    formState: { isDirty, dirtyFields }
  } = useForm<EventDoc>({
    defaultValues: {
      Text: pendingData?.Text ?? event.Text,
      TaskType: pendingData?.TaskType ?? event.TaskType ?? TaskType.meet,
      AllDay: (pendingData?.AllDay ?? event.AllDay) ? event.AllDay : '0',
      TimeZoneNameID:
        (pendingData?.TimeZoneNameID ?? (event.TimeZoneNameID?.length ?? 0) > 0)
          ? event.TimeZoneNameID
          : getDefaultTimezone(),
      StartDate:
        !event.Recurrence && event.late === false
          ? event.StartDate
          : (
              (createAtStart ??
                generateStartEndBasedOnTime(dayjs().unix(), '').start * 1000) /
              1000
            ).toString(),
      EndDate:
        !event.Recurrence && event.late === false
          ? event.EndDate
          : (
              (createAtEnd ??
                generateStartEndBasedOnTime(dayjs().unix(), '').end * 1000) /
              1000
            ).toString(),
      Status: event.Status ?? EventStatus.ACTIVE,
      Recurrence: pendingData?.Recurrence ?? event.Recurrence,
      Location: pendingData?.Location ?? event.Location,
      Reminder: pendingData?.Reminder ?? event.Reminder ?? [],
      Notes: pendingData?.Notes ?? event.Notes ?? [],
      attendees: pendingData?.attendees ?? event.attendees ?? [],
      attendeesChanged: false,
      RequestConfirmation:
        pendingData?.RequestConfirmation ?? event.RequestConfirmation ?? '0',
      GroupID: pendingData?.GroupID ?? event.GroupID,
      ThirdPartyID: pendingData?.ThirdPartyID ?? event.ThirdPartyID,
      PersonalOwnerId: pendingData?.PersonalOwnerId ?? event.PersonalOwnerId,
      OwnerID: pendingData?.OwnerID ?? event.OwnerID,
      Label: pendingData?.Label ?? event.Label,
      Type: pendingData?.Type ?? event.Type,
      someday: pendingData?.someday ?? event.someday ?? '0',
      Priority: pendingData?.Priority ?? event.Priority,
      createAtStart: createAtStart,
      createAtEnd: createAtEnd,
      noteText: '',
      id: event.id,
      locationPendingText: '',
      notePendingText: '',
      notePendingBytes: undefined,
      SupplementaryGroupsIDs:
        pendingData?.SupplementaryGroupsIDs ??
        event.SupplementaryGroupsIDs ??
        [],
      Attachments: event.Attachments,
      CustomUrl: event.CustomUrl,
      LocationReminders:
        pendingData?.LocationReminders ?? event.LocationReminders ?? []
    }
  })

  useEffect(() => {
    if (
      (!isDirty && event._id && event._id.length > 0) ||
      (event.local_id && event.local_id.length > 0)
    ) {
      reset({
        Text: event.Text,
        AllDay: event.AllDay ? event.AllDay : '0',
        TimeZoneNameID:
          (event.TimeZoneNameID?.length ?? 0) > 0
            ? event.TimeZoneNameID
            : getDefaultTimezone(),
        StartDate:
          !event.Recurrence && event.late === false
            ? event.StartDate
            : (createAtStart / 1000).toString(),
        EndDate:
          !event.Recurrence && event.late === false
            ? event.EndDate
            : (createAtEnd / 1000).toString(),
        Status: event.Status ?? EventStatus.ACTIVE,
        Recurrence: event.Recurrence,
        Location: event.Location,
        Reminder: event.Reminder ?? [],
        Notes: event.Notes ?? [],
        attendees: event.attendees ?? [],
        attendeesChanged: false,
        RequestConfirmation: event.RequestConfirmation ?? '0',
        GroupID: event.GroupID,
        ThirdPartyID: event.ThirdPartyID,
        Label: event.Label,
        Type: event.Type,
        someday: event.someday,
        Priority: event.Priority,
        createAtStart: createAtStart,
        createAtEnd: createAtEnd,
        TaskType: event.TaskType ?? TaskType.meet,
        noteText: '',
        locationPendingText: '',
        notePendingText: '',
        notePendingBytes: undefined,
        SupplementaryGroupsIDs: event.SupplementaryGroupsIDs ?? [],
        Attachments: event.Attachments,
        CustomUrl: event.CustomUrl,
        LocationReminders: event.LocationReminders ?? []
      })
    }
  }, [event._rev, event.AllDay])

  function cacheEventData() {
    LocalStorage.set(
      LocalStorageKey.DUPLICATE_EVENT_DATA,
      JSON.stringify({ ...getValues(), newEvent: newEvent })
    )
    LocalStorage.set(
      LocalStorageKey.PENDING_EVENT_DATA,
      JSON.stringify({ ...getValues(), newEvent: newEvent })
    )
  }

  Logger.orange('EventDetails:Values', getValues())

  const originalGroupId = event.GroupID
  const originalThirdPartyId = event.ThirdPartyID
  const originalOwnerId = event.OwnerID
  const [calendarMoveRequest, setCalendarMoveRequest] = useState<
    CalendarMoveRequest | undefined
  >(undefined)

  function updateCalendarMoveRequest(nextValues: Partial<EventDoc>) {
    if (!event._id) return

    const nextGroupId = nextValues.GroupID ?? getValues('GroupID')
    const nextThirdPartyId =
      nextValues.ThirdPartyID ?? getValues('ThirdPartyID')
    const nextOwnerId = nextValues.OwnerID ?? getValues('OwnerID')

    const isMovePending =
      nextGroupId !== originalGroupId ||
      nextThirdPartyId !== originalThirdPartyId

    if (!isMovePending) {
      setCalendarMoveRequest(undefined)
      return
    }

    setCalendarMoveRequest({
      oldTaskId: event._id ?? '',
      originalGroupId,
      originalThirdPartyId,
      originalOwnerId,
      nextGroupId,
      nextThirdPartyId,
      nextOwnerId
    })
  }

  function applyEventOverrides(overrides?: Partial<EventDoc>) {
    if (!overrides) return

    Object.entries(overrides).forEach(([key, value]) => {
      setValue(key as keyof EventDoc, value as any, { shouldDirty: true })
    })

    updateCalendarMoveRequest(overrides)
  }

  const [participantsStatus, setParticipantStatus] = useState<
    Record<string, EventParticipantStatus> | undefined
  >(event?.ParticipantsStatus)
  const setGroupID = (id: string, options?: SetGroupIDOptions) => {
    setValue('GroupID', id, { shouldDirty: true })
    applyEventOverrides(options?.eventOverrides)
    updateCalendarMoveRequest({ GroupID: id, ...options?.eventOverrides })

    if (newEvent && options?.skipLocalWrite !== true) {
      if (event.syncStatus != 'deleted')
        Database.write(async () => {
          return (await localEventByLocalId(event.id ?? ''))?.update(
            (local) => {
              local.GroupID = id
            }
          )
        })
    }

    cacheEventData()
  }
  const setLocation = (location: EventLocation | undefined) => {
    if (location === undefined) {
      setValue('Location', {}, { shouldDirty: true })
    } else {
      setValue('Location', location, { shouldDirty: true })
    }

    cacheEventData()

    setValue('locationPendingText', '', { shouldDirty: true })
  }

  const supplementaryGroupIds = getValues('SupplementaryGroupsIDs')

  const setSupplementaryGroupIds = (ids: string[] | undefined) => {
    setValue('SupplementaryGroupsIDs', ids, { shouldDirty: true })
  }

  const someday = getValues('someday')

  const setSomeday = (someday: string) => {
    setValue('TimeZoneNameID', getDefaultTimezone(), { shouldDirty: false })

    if (someday === '0') {
      const createAt =
        createAtStart > 0
          ? createAtStart
          : generateStartEndBasedOnTime(dayjs().unix(), '').start * 1000

      const newStartTime = dayjs(createAt).set('minute', 0).set('second', 0)

      setAllDay('0')
      setStartTime(newStartTime.valueOf())
      setEndTime(newStartTime.add(30, 'minute').valueOf())
    }

    setValue('someday', someday, { shouldDirty: true })
    setValue('Recurrence', undefined, { shouldDirty: true })
    setValue('Reminder', [], { shouldDirty: true })

    // Database.write(async () => {
    //   const localEvent = await localEventByLocalId(event.id ?? '')
    //   if (!localEvent) return

    //   return localEvent.update((local) => {
    //     local.someday = someday
    //   })
    // })

    cacheEventData()
  }

  const setTimeZone = (tz: string | undefined | null) => {
    setValue('TimeZoneNameID', tz ?? getDefaultTimezone(), {
      shouldDirty: true
    })
  }
  const setStatus = (status: string) => {
    Logger.pink('EventPopup:setStatus', status)
    setValue('Status', status as EventStatus, { shouldDirty: true })
  }

  const setName = (name: string) => {
    setValue('Text', name, { shouldDirty: true })
    cacheEventData()
  }

  const setThirdPartyId = (id: string) => {
    setValue('ThirdPartyID', id, { shouldDirty: false })
    updateCalendarMoveRequest({ ThirdPartyID: id })
    cacheEventData()
  }

  const setType = (type: DocType) => {
    setValue('Type', type, { shouldDirty: true })
    // setNotes([])
    setPriority('1')
    setRSVP('0')
    setTaskType(TaskType.meet)
    if (type === DocType.EVENT) {
      getDefaultCalendar().then((group) => {
        if (group) {
          Logger.orange('EventPopup:setType', group)
          setGroupID(group._id)
          setTaskType(TaskType.personalEvent)
          setSelectedColor(group.GroupColor)

          setRemindersToEvent(
            getValues('Reminder')?.filter(
              (r) => r.isRelativeReminder === '1'
            ) || []
          )
          const thirdPartyId = groupToThirdPartyCalendarId(group)
          if (thirdPartyId) {
            setThirdPartyId(thirdPartyId)
          }
        }
      })
    } else {
      setGroupID('')
      setThirdPartyId('')
    }
    cacheEventData()
  }

  const setPriority = (priority: string) => {
    setValue('Priority', priority, { shouldDirty: true })
    cacheEventData()
  }

  const [attendeeText, setAttendeeText] = useState<string>('')
  const popoverReattachStateRef = useRef<{
    id: number
    originalShouldKeepPopup: string | null
    active: boolean
  }>({
    id: 0,
    originalShouldKeepPopup: null,
    active: false
  })

  function finishPopoverReattach(reattachId: number) {
    const reattachState = popoverReattachStateRef.current
    if (reattachState.id !== reattachId) return

    if (reattachState.originalShouldKeepPopup !== '1') {
      LocalStorage.remove(LocalStorageKey.SHOULD_KEEP_POPUP)
    }

    popoverReattachStateRef.current = {
      ...reattachState,
      active: false
    }
  }

  /**
   * Build every plausible DOM container id for the updated event.
   *
   * The rendered id (see {@link eventIdForContainer}) depends on `event.Text`,
   * `event.AllDay` and `event.TimeZoneNameID`. After {@link updatePendingEventInCalendarApi}
   * the calendar re-renders with the NEW `instanceStart` but the underlying
   * `EventDoc` model (via `getEventModel`) is still stale until the DB
   * observer fires — so we have to look for ids built from both the old
   * model state and the new form values.
   */
  function buildEventDomCandidates(updatedEvent: EventDoc): string[] {
    const instanceStart = Number(updatedEvent.StartDate) * 1000
    if (!Number.isFinite(instanceStart)) return []

    const textVariants = new Set<string | undefined>([
      event.Text,
      getValues('Text')
    ])
    const allDayVariants = new Set<string | undefined>([
      event.AllDay,
      updatedEvent.AllDay
    ])
    const tzVariants = new Set<string | undefined>([
      event.TimeZoneNameID,
      updatedEvent.TimeZoneNameID
    ])

    const ids = new Set<string>()
    for (const Text of textVariants) {
      for (const AllDay of allDayVariants) {
        for (const TimeZoneNameID of tzVariants) {
          const id = eventIdForContainer(
            { ...updatedEvent, Text, AllDay, TimeZoneNameID } as EventDoc,
            instanceStart
          )
          if (id) ids.add(id)
        }
      }
    }
    return [...ids]
  }

  function findEventElementByIds(ids: string[]): HTMLElement | null {
    for (const id of ids) {
      const el = document.getElementById(id)
      if (el instanceof HTMLElement) return el
    }
    return null
  }

  /**
   * Reattach the open popover to the (now moved) event element.
   *
   * The element id can change in two independent ways after a time edit:
   *   1. {@link updatePendingEventInCalendarApi} mutates FullCalendar's
   *      internal store via `setDates`, which triggers a synchronous
   *      re-render with the NEW `instanceStart` but the OLD model.
   *   2. The DB observer pipeline eventually pushes a fresh `eventModelMap`
   *      so `Event.tsx` re-renders with the NEW model.
   *
   * Instead of polling on a fixed interval, we wait for any matching id to
   * appear in the calendar subtree via `MutationObserver`. This resolves on
   * the very next paint in the common case and degrades gracefully if the
   * element never materialises.
   */
  function reattachPopoverToEvent(updatedEvent: EventDoc, reattachId: number) {
    if (popoverReattachStateRef.current.id !== reattachId) return

    const candidateIds = buildEventDomCandidates(updatedEvent)
    if (candidateIds.length === 0) {
      finishPopoverReattach(reattachId)
      return
    }

    const tryResolve = (): boolean => {
      if (popoverReattachStateRef.current.id !== reattachId) return true
      const el = findEventElementByIds(candidateIds)
      if (!el) return false
      pointTo(el, el.id)
      finishPopoverReattach(reattachId)
      return true
    }

    if (tryResolve()) return

    const calendarApi = (calendar.ref as any)?.current?.getApi?.()
    const observerRoot: HTMLElement =
      (calendarApi?.el as HTMLElement | undefined) ?? document.body

    let observer: MutationObserver | null = null
    let timeoutId: number | null = null

    const teardown = () => {
      if (observer) {
        observer.disconnect()
        observer = null
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
        timeoutId = null
      }
    }

    observer = new MutationObserver(() => {
      if (tryResolve()) teardown()
    })
    observer.observe(observerRoot, { childList: true, subtree: true })

    timeoutId = window.setTimeout(() => {
      teardown()
      if (popoverReattachStateRef.current.id === reattachId) {
        // Best-effort: hand the most likely id to the popover provider,
        // which has its own short retry window.
        pointTo(null, candidateIds[0])
        finishPopoverReattach(reattachId)
      }
    }, 1000)
  }

  function updatePendingEventInCalendarApi(updatedEvent: EventDoc) {
    const calendarApi = (calendar.ref as any)?.current?.getApi?.()
    if (!calendarApi) {
      return
    }

    const stableFcId = getFullCalendarEventId(updatedEvent)

    const startSeconds = Number(updatedEvent.StartDate)
    const endSeconds = Number(updatedEvent.EndDate)
    const byPassedFcId =
      (fcEventId?.length ?? 0) > 0
        ? calendarApi.getEventById(fcEventId)
        : undefined

    const byStableId =
      stableFcId.length > 0 ? calendarApi.getEventById(stableFcId) : undefined

    const matchedEvent = byPassedFcId ?? byStableId

    if (!matchedEvent) {
      Logger.orange(
        'updatePendingEventInCalendarApi: no matching FC event',
        fcEventId,
        stableFcId,
        event,
        event.id,
        updatedEvent,
        calendarApi.getEvents().map((e: any) => e?.id)
      )
      return
    } else {
      Logger.orange(
        'updatePendingEventInCalendarApi: matched FC event',
        matchedEvent
      )
    }

    const isAllDay = updatedEvent.AllDay === '1'

    if (!Number.isFinite(startSeconds) || !Number.isFinite(endSeconds)) {
      return
    }

    const startInput = isAllDay
      ? dayjs.utc(startSeconds * 1000).format('YYYY-MM-DD')
      : new Date(startSeconds * 1000)

    const allDayDaysDiff = (endSeconds - startSeconds) / 60 / 60 / 24
    const endInput = isAllDay
      ? dayjs
          .utc(endSeconds * 1000)
          .add(allDayDaysDiff > 0 ? 1 : 0, 'day')
          .format('YYYY-MM-DD')
      : new Date(endSeconds * 1000)

    try {
      matchedEvent.setDates(startInput, endInput, {
        allDay: isAllDay
      })
    } catch (err) {
      Logger.orange('updatePendingEventInCalendarApi: setDates threw', err)
    }
  }

  async function updateTimeInDB() {
    if (newEvent) {
      const previousReattachState = popoverReattachStateRef.current
      const reattachId = previousReattachState.id + 1

      popoverReattachStateRef.current = {
        id: reattachId,
        originalShouldKeepPopup: previousReattachState.active
          ? previousReattachState.originalShouldKeepPopup
          : LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP),
        active: true
      }

      const updatedEvent = {
        ...event,
        AllDay: getValues('AllDay'),
        StartDate: getValues('StartDate'),
        EndDate: getValues('EndDate'),
        TimeZoneNameID: getValues('TimeZoneNameID')
      } as EventDoc

      LocalStorage.set(LocalStorageKey.SHOULD_KEEP_POPUP, '1')

      // Keep UI responsive while DB observation pipeline catches up.
      updatePendingEventInCalendarApi(updatedEvent)

      if (event.syncStatus != 'deleted')
        Database.write(async () => {
          return (await localEventByLocalId(event.id ?? ''))?.update(
            (local) => {
              Logger.blue('Updating time in DB', local)
              local.AllDay = getValues('AllDay')
              local.StartDate = getValues('StartDate')
              local.EndDate = getValues('EndDate')
            }
          )
        })
      cacheEventData()
      reattachPopoverToEvent(updatedEvent, reattachId)
    }
  }

  const setAllDay = (allDay: string) => {
    const isAllDayNow = allDay === '1'
    const nowInUserTz = dayjs().tz(getDefaultTimezone())
    const fallbackStartUnix = nowInUserTz.startOf('hour').add(1, 'hour').unix()
    const fallbackEndUnix = fallbackStartUnix + 60 * 60

    const parsedStartUnix = Number(getValues('StartDate'))
    const parsedEndUnix = Number(getValues('EndDate'))
    const safeStartUnix = Number.isFinite(parsedStartUnix)
      ? parsedStartUnix
      : fallbackStartUnix
    const safeEndUnix = Number.isFinite(parsedEndUnix)
      ? parsedEndUnix
      : fallbackEndUnix

    setValue('AllDay', allDay, { shouldDirty: true })
    setTimeZone(allDay === '1' ? 'UTC' : getDefaultTimezone())

    if (isAllDayNow) {
      const currentTz = getDefaultTimezone()
      const startDate = dayjs.unix(safeStartUnix).tz(currentTz)
      const endDate = dayjs.unix(safeEndUnix).tz(currentTz)

      const startOfDay = dayjs
        .tz(startDate.format('YYYY-MM-DD'), 'UTC')
        .startOf('day')
      const endOfDay = dayjs
        .tz(endDate.format('YYYY-MM-DD'), 'UTC')
        .startOf('day')

      // Write values directly instead of calling setStartTime/setEndTime,
      // each of which would trigger its own updateTimeInDB. Multiple
      // back-to-back reattach attempts race each other and the popover
      // never reaches the final all-day cell.
      setValue(
        'StartDate',
        Math.floor(startOfDay.valueOf() / 1000).toString(),
        {
          shouldDirty: true
        }
      )
      setValue('EndDate', Math.floor(endOfDay.valueOf() / 1000).toString(), {
        shouldDirty: true
      })
    } else {
      const eventStart = dayjs.unix(safeStartUnix)
      const eventEnd = dayjs.unix(safeEndUnix)
      const durationDays = eventEnd.diff(eventStart, 'day')

      console.log('durationDays', durationDays)

      const startDateUTC = dayjs.unix(safeStartUnix).tz('UTC')

      const nowHour = dayjs()
        .tz(getDefaultTimezone())
        .startOf('hour')
        .add(1, 'hour')
        .hour()
      const nowMinute = 0

      const startOfDay = dayjs
        .tz(startDateUTC.format('YYYY-MM-DD'), getDefaultTimezone())
        .set('hour', nowHour)
        .set('minute', nowMinute)
      const endOfDay = startOfDay
        .clone()
        .add(1, 'hour')
        .add(durationDays, 'day')

      console.log(
        startOfDay.format('YYYY-MM-DD HH:mm:ss'),
        endOfDay.format('YYYY-MM-DD HH:mm:ss')
      )

      // Write values directly — see comment in the all-day branch above.
      setValue(
        'StartDate',
        Math.floor(startOfDay.valueOf() / 1000).toString(),
        {
          shouldDirty: true
        }
      )
      setValue('EndDate', Math.floor(endOfDay.valueOf() / 1000).toString(), {
        shouldDirty: true
      })
    }

    setEventReminders([
      generateDefaultReminder(isAllDayNow, getValues('StartDate'))
    ])

    updateTimeInDB()
  }

  const ownerId = getValues('OwnerID')

  /**
   * Imperatively notifies FullCalendar that the matching event card needs to
   * re-render its content. FullCalendar will not re-invoke `eventContent` for
   * an event whose start/end did not change (extendedProps mutations alone do
   * not invalidate rendered cells). Mirrors the role `setDates` plays in the
   * time-change path: a synchronous, FC-API-level nudge that keeps the UI in
   * sync with the just-edited form state until the DB observation pipeline
   * catches up.
   */
  function refreshOwnerInCalendarApi(newOwnerId: string) {
    const calendarApi = (calendar.ref as any)?.current?.getApi?.()
    if (!calendarApi) return

    const idCandidates = [
      `${event._id ?? ''}${event.local_id ?? ''}`,
      `${event.id ?? ''}${event.local_id ?? ''}`,
      `${event.local_id ?? ''}`,
      `${event.id ?? ''}`
    ].filter((c) => c.length > 0)

    const byExactId = idCandidates
      .map((id) => calendarApi.getEventById(id))
      .find((fcEvent: any) => !!fcEvent)

    const fallback = byExactId
      ? null
      : calendarApi.getEvents().find((fcEvent: any) => {
          const fcId = `${fcEvent?.id ?? ''}`
          return (
            fcId === 'undefined' ||
            idCandidates.some((suffix) => fcId.endsWith(suffix))
          )
        })

    const matchedEvent = byExactId ?? fallback
    Logger.red('refreshOwnerInCalendarApi: matched FC event', matchedEvent)
    if (!matchedEvent) return

    try {
      matchedEvent.setExtendedProp('OwnerID', newOwnerId)
      // FC will not re-invoke eventContent for an event whose start/end and
      // identity-level props did not change, even if extendedProps or the
      // backing EventInput object reference was swapped. Time-change works
      // because updatePendingEventInCalendarApi calls setDates. Mirror that
      // here by calling setDates with the SAME dates — it is a no-op
      // semantically but forces FC to re-render this card's content.
      const sameStart = matchedEvent.start
      const sameEnd = matchedEvent.end
      const sameAllDay = matchedEvent.allDay
      if (sameStart) {
        matchedEvent.setDates(sameStart, sameEnd ?? undefined, {
          allDay: sameAllDay
        })
      }
    } catch (err) {
      Logger.orange('refreshOwnerInCalendarApi: setExtendedProp threw', err)
    }
  }

  const setOwnerId = (id: string, options?: SetOwnerIDOptions) => {
    setValue('OwnerID', id, { shouldDirty: true })
    updateCalendarMoveRequest({ OwnerID: id })
    cacheEventData()

    // Immediate FC-level refresh so month/week/day views update before DB
    // observers fire (same pattern as updateTimeInDB → setDates). This nudge
    // alone is not enough though: the Event component reads the underlying
    // model from CalendarView's eventModelMap, which is only rebuilt when
    // mapEventsToCalendar re-runs in response to a Watermelon observer.
    refreshOwnerInCalendarApi(id)

    // Write to local DB for both new and existing events so the observer
    // chain refreshes eventModelMap. Without this, existing-event owner
    // changes are invisible until Save (which performs the same write).
    //
    // Important: `event` arrives here as a Database.toServerObject snapshot
    // (created in CalendarView's mapEventsToCalendar), which omits the
    // Watermelon `id` field. We therefore must look up by `_id` (server id)
    // first, and only fall back to `id` for brand-new events that have not
    // received a server id yet.
    if (event.syncStatus !== 'deleted' && options?.writeLocal !== false) {
      Logger.red('setOwnerId: starting DB write', {
        newOwnerId: id,
        event_id: event._id,
        eventId: event.id,
        currentOwnerOnSnapshot: event.OwnerID
      })

      Database.write(async () => {
        const local =
          (event._id && event._id.length > 0
            ? await localEventById(event._id)
            : undefined) ?? (await localEventByLocalId(event.id ?? ''))

        if (!local) {
          Logger.orange('setOwnerId: no local event found, skipping DB write', {
            _id: event._id,
            id: event.id
          })
          return
        }

        Logger.red('setOwnerId: matched local record, applying update', {
          localId: local.id,
          local_id: (local as any)._id,
          beforeOwnerID: (local as any).OwnerID,
          newOwnerID: id
        })

        return local.update((rec) => {
          rec.OwnerID = id
          // Bump LastUpdate to guarantee Watermelon detects the write even
          // if OwnerID happens to equal the current value already on the
          // record (which can happen when reassigning back to the same
          // member). Without an actual column delta, observeWithColumns
          // would not fire and the calendar would stay stale.
          rec.LastUpdate = dayjs().unix().toString()
        })
      }).then(() => {
        Logger.red('setOwnerId: DB write completed for OwnerID', id)
      })
    }
  }
  const startTime = getValues('StartDate')
  const setStartTime = (time: number) => {
    let zoned = dayjs(time)

    if (getValues('AllDay') === '1') {
      // Snap to UTC midnight so the stored instant always represents the
      // selected calendar date in any timezone. Without this, dates picked
      // via DatePickPopup (constructed at 13:00 UTC) would render as the
      // next day in TZs >= UTC+11.
      zoned = dayjs(time).tz('UTC').startOf('day')
    }

    Logger.orange('NewStartDate', zoned.format('YYYY-MM-DD HH:mm:ss'))

    const duration =
      Number(getValues('EndDate')) - Number(getValues('StartDate'))

    const newStartDate = zoned.unix()

    setValue('StartDate', newStartDate.toString(), {
      shouldDirty: true
    })

    if (getValues('TaskType') === TaskType.personalEvent && duration === 0) {
      setValue('EndDate', newStartDate.toString(), {
        shouldDirty: true
      })
    } else {
      setValue('EndDate', (newStartDate + duration).toString(), {
        shouldDirty: true
      })
    }

    updateTimeInDB()
  }
  const taskType = getValues('TaskType') ?? TaskType.meet
  const setTaskType = (type: TaskType) => {
    setValue('TaskType', type, { shouldDirty: true })
    // setNotes([])
    setPriority('1')
    setRSVP('0')
    setThirdPartyId('')
    cacheEventData()
  }
  const endTime = getValues('EndDate')
  const setEndTime = (time: number) => {
    const zoned = dayjs(time).tz(
      getValues('TimeZoneNameID') ?? getDefaultTimezone()
    )

    const duration =
      Number(getValues('EndDate')) - Number(getValues('StartDate'))

    const startDate = dayjs
      .unix(Number(getValues('StartDate')))
      .tz(getValues('TimeZoneNameID') ?? getDefaultTimezone())

    const newEndDateJs = zoned
      .tz(getValues('TimeZoneNameID') ?? getDefaultTimezone())
      .set('hour', zoned.get('hour'))
      .set('minute', zoned.get('minute'))
      .set('date', zoned.get('date'))

    let newEndDate = newEndDateJs.unix()

    Logger.blue(
      'SetEndTime:',
      dayjs.unix(newEndDate).format('YYYY-MM-DD HH:mm:ss')
    )
    Logger.blue('SetEndTime:', startDate.format('YYYY-MM-DD HH:mm:ss'))

    let isEndDateBeforeStartDate = false

    if (getValues('AllDay') === '1') {
      const endDayOfYear = dayjs.unix(newEndDate).dayOfYear()
      const startDayOfYear = startDate.dayOfYear()
      isEndDateBeforeStartDate = endDayOfYear < startDayOfYear
    } else {
      isEndDateBeforeStartDate = newEndDate < Number(getValues('StartDate'))
    }

    if (isAMPM() && isEndDateBeforeStartDate) {
      if (startDate.get('hour') <= 12 && newEndDateJs.get('hour') <= 12) {
        newEndDate = newEndDateJs.set('hour', zoned.get('hour') + 12).unix()
        isEndDateBeforeStartDate = false
      }
    }

    if (isEndDateBeforeStartDate) {
      setValue('StartDate', (newEndDate - duration).toString(), {
        shouldDirty: true
      })
    }

    setValue('EndDate', newEndDate.toString(), {
      shouldDirty: true
    })
    updateTimeInDB()
  }

  const eventReminders = getValues('Reminder')
  const setEventReminders = (eventReminders: EventReminder[]) => {
    setValue('Reminder', eventReminders, { shouldDirty: true })

    cacheEventData()
  }

  const labels = getValues('Label')
  const setLabels = (labels: SimpleLabel[]) => {
    setValue('Label', labels, { shouldDirty: true })

    cacheEventData()
  }

  const RSVP = getValues('RequestConfirmation')
  const setRSVP = (requestParticipation: string) => {
    setValue('RequestConfirmation', requestParticipation, { shouldDirty: true })
    cacheEventData()
  }
  const notes = getValues('Notes')
  const setNotes = (notes: EventNote[]) => {
    const sanitizedNotes = notes.filter((note) => {
      if (note.NoteType === '1') return true
      return clearNote(note.Note).trim().length > 0
    })

    Logger.orange('setNotes', sanitizedNotes)
    setValue('Notes', sanitizedNotes, { shouldDirty: true })

    if (newEvent)
      Database.write(async () => {
        ;(await localEventByLocalId(event.id ?? ''))?.update((local) => {
          local.Notes = sanitizedNotes
        })
      })
  }
  const attendees = getValues('attendees')
  const setAttendees = (attendees: Attendee[], shouldDirty?: boolean) => {
    setValue(
      'attendeesChanged',
      !arraysAreEqual(event.attendees ?? [], attendees),
      {
        shouldDirty: true
      }
    )
    setValue('attendees', attendees, { shouldDirty: shouldDirty ?? false })
    console.log(attendees)

    cacheEventData()
  }

  const setRecurrence = (eventRecurrence: EventRecurrence, dirty?: boolean) => {
    setValue('Recurrence', eventRecurrence, { shouldDirty: dirty ?? true })
    if (newEvent && typeof event.update === 'function' && dirty) {
      Database.write(async () => {
        event.update((local: { Recurrence: EventRecurrence }) => {
          local.Recurrence = eventRecurrence
        })
      })
    }

    cacheEventData()
  }

  const [loading, setLoading] = useState(false)
  const [openDiscard, setOpenDiscard] = useState(false)
  const [openAddReminder, setOpenAddReminder] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    LocalStorage.set(
      LocalStorageKey.SHOULD_KEEP_POPUP,
      openAddReminder ? '1' : '0'
    )
  }, [openAddReminder])

  const setRemindersToEvent = (reminders: EventReminder[]) => {
    setOpenAddReminder(false)
    setTimeout(() => {
      setEventReminders(reminders)
    }, 50)
  }
  const calendar = useCalendar()
  const calendarSync = useCalendarSync()

  async function isLocalSaveOnly() {
    const local = await localEventById(event._id ?? '')
    const isConvertedGroup = isConvertedGroupId(getValues('GroupID') ?? '')

    if (isConvertedGroup) return false

    return (
      local?.ThirdPartyID?.length === 0 &&
      Object.keys(dirtyFields).length === 1 &&
      dirtyFields.Reminder !== undefined
    )
  }

  async function sendEventToServer(data: EventDoc) {
    setLoading(true)
    /**
     * This field determines, if an event should be updated locally only, without touching the server
     * For example, we save reminders only locally
     */

    Logger.blue('sendEventToServer:dirtyfields', dirtyFields)
    const shouldSaveLocally = await isLocalSaveOnly()

    // no catch here, we handle errors upstream in the calendarSync.sendEventToServer function
    await calendarSync
      .sendEventToServer(
        event,
        data,
        shouldSaveLocally,
        isDirty || LocalStorage.get(LocalStorageKey.PENDING_EVENT_DATA) !== null
      )
      .then(() => {
        setTimeout(() => {
          if (!isCypress()) {
            const newDate = dayjs(
              isEventRecurrenceValid(data, dayjs(data.createAtStart))
                ? data.createAtStart
                : Number(data.StartDate) * 1000
            ).tz(data.TimeZoneNameID)
            if (
              newDate.isAfter(dayjs(calendar.toISO)) ||
              newDate.isBefore(dayjs(calendar.fromISO))
            ) {
              calendar.onGoToDate(newDate.format('YYYY-MM-DD'))
            }
            calendar.onScrollToTime(newDate)
          }

          UserSettings().then(async (us) => {
            const shouldSwitchToAGroup =
              ((isGroupHidden(us, getValues('GroupID') ?? '') ||
                isGroupHidden(us, calendarMoveRequest?.nextGroupId ?? '')) &&
                isTrulyEmpty(
                  LocalStorage.get(LocalStorageKey.SELECTED_GROUP)
                )) ||
              !isTrulyEmpty(LocalStorage.get(LocalStorageKey.SELECTED_GROUP))

            let groupToSwitch = getValues('GroupID') ?? ''

            if (calendarMoveRequest) {
              groupToSwitch = calendarMoveRequest.nextGroupId ?? groupToSwitch
            }

            Logger.pink(
              'Move:',
              calendarMoveRequest,
              shouldSwitchToAGroup,
              groupToSwitch,
              getValues()
            )

            if (shouldSwitchToAGroup) {
              if (
                isUserActiveInGroup(
                  await localGroupById(groupToSwitch),
                  LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
                )
              ) {
                /**
                 * we need to switch ui to the proper calendar if the event is hidden in the current one
                 * this can happen when user starts to add an event in one calendar but changing it to
                 * another
                 */
                calendar.onGroupServer(groupToSwitch)
              }
            }
          })
        }, 250)
      })
  }

  const [titleSet, onTitleSet] = useState(false)

  const addNoteToArray = (noteText: string, bytes?: string) => {
    if (!noteText) return

    let currentNotes = getValues('Notes')

    if (typeof currentNotes === 'string') {
      currentNotes = []
    }

    const isNoteWithMetaData = currentNotes?.find((note) =>
      note.Note.includes(META_DATA_LABEL)
    )

    if (isNoteWithMetaData) {
      const updatedNote = currentNotes?.map((note) => {
        if (note.Note.includes(META_DATA_LABEL)) {
          return {
            ...note,
            Note: appendNoteWithMetaData(noteText, unPackMetaData(event))
          }
        }

        return note
      })

      setNotes(updatedNote ?? [])
    } else {
      const newNotes = [...(currentNotes ?? [])]
      const newNote: EventNote = {
        FilePath: 'null',
        Note: noteText,
        NoteID: Date.now(),
        Status: 1,
        FileBytes: bytes
      }

      if (getValues('Type') === DocType.NOTE && !titleSet) {
        let updatedText = noteText
          .replace(/<\/?[^>]+(>|$)/g, '')
          .substring(0, 25)

        if (noteText.length > 25) {
          updatedText += '...'
        }
        if (updatedText != '...') setName(updatedText)
      }

      newNotes.push(newNote)
      setNotes(newNotes)
    }
  }

  const updateNote = (updatedNote: EventNote) => {
    const currentNotes = getValues('Notes')
    if (currentNotes) {
      const updated = currentNotes.map((note) => {
        if (note.NoteID === updatedNote.NoteID) return updatedNote

        return note
      })
      setNotes(updated)
      const noteText = updatedNote.Note

      if (getValues('Type') === DocType.NOTE && !titleSet) {
        let updatedText = noteText
          .replace(/<\/?[^>]+(>|$)/g, '')
          .substring(0, 25)

        if (noteText.length > 25) {
          updatedText += '...'
        }

        if (updatedText != '...') setName(updatedText)
      }
    }
  }

  const addAttendeeToArray = (newAttendee: Attendee) => {
    if (
      !newAttendee ||
      attendees?.find((attendee) => attendee.email === newAttendee.email)
    )
      return

    const newAttendees = [...(getValues('attendees') ?? [])]

    Database.getCollection<ParticipantSuggestion>(
      CollectionType.PARTICIPANT_SUGGESTION
    )
      .query()
      .fetch()
      .then((participants) => {
        const participant = participants.find(
          (p) => p.email === newAttendee.email
        )
        if (!participant && newAttendee.email) {
          Database.write(async () => {
            await Database.getCollection<ParticipantSuggestion>(
              CollectionType.PARTICIPANT_SUGGESTION
            ).create((p) => {
              p.email = newAttendee.email!
              p.name = newAttendee.displayName ?? ''
              p.img = ''
            })
          })
        }
      })

    const provider = groupToProvider(group)
    if (provider === ProviderType.GOOGLE || provider === ProviderType.APPLE) {
      //first add yourself if not exist
      if (
        attendees?.find(
          (attendee) =>
            attendee.email === removePrefix(group?.CalendarEmail ?? '')
        ) === undefined
      ) {
        newAttendees.push({
          email: removePrefix(group?.CalendarEmail ?? ''),
          responseStatus: 'accepted',
          self: true
        })
      }
    }

    if (!newAttendees?.find((attendee) => attendee.email === newAttendee.email))
      newAttendees.push(newAttendee)

    setAttendeeText('')
    setAttendees(newAttendees)
  }
  const noteText = getValues('noteText')
  const setNoteText = (text: string) => {
    setValue('noteText', text, { shouldDirty: false })
  }

  const attachements = getValues('Attachments')
  const setAttachments = (attachments: Attachment[] | undefined) => {
    setValue('Attachments', attachments, { shouldDirty: true })
  }
  const customUrl = getValues('CustomUrl')
  const setCustomUrl = (url: string | undefined) => {
    setValue('CustomUrl', url, { shouldDirty: true })
  }

  const setLocationPendingText = (text: string) => {
    setValue('locationPendingText', text, { shouldDirty: true })
  }

  const setNotePendingText = (text: string) => {
    setValue('notePendingText', text, { shouldDirty: true })
  }
  const setNotePendingBytes = (bytes: string | undefined) => {
    setValue('notePendingBytes', bytes, { shouldDirty: true })
  }

  const addLocationToEvent = (text: string) => {
    return googleMapsService.fromAddress(text).then(
      (response) => {
        const { lat, lng } = response.results[0].geometry.location
        setLocation({
          FilteredLocation: 'null',

          Address: text,
          Lat: String(lat),
          Long: String(lng)
        })
      },
      (error) => {
        setLocation({
          FilteredLocation: 'null',

          Address: text,
          Lat: '0',
          Long: '0'
        })
        console.log(getValues())
      }
    )
  }

  const applyPendingData = async () => {
    const locationPendingText = getValues('locationPendingText')
    const notePendingText = getValues('notePendingText')
    const notePendingBytes = getValues('notePendingBytes')

    if (
      locationPendingText.length > 0 &&
      getValues('Location')?.Address !== locationPendingText
    ) {
      await addLocationToEvent(locationPendingText)
    }
    if (notePendingText.length > 0) {
      if (notePendingBytes) {
        addNoteToArray(notePendingText, notePendingBytes)
        setValue('notePendingText', '', { shouldDirty: true })
        setValue('notePendingBytes', undefined, { shouldDirty: true })
        return
      }
      addNoteToArray(notePendingText)
      setValue('notePendingText', '', { shouldDirty: true })
    }
  }

  const [selectedColor, setSelectedColor] = useState<string | undefined>(
    colorFromMetaData(event) ?? event.Color ?? APP_COLOR
  )

  const eventPopupInterface: IEventDetails = {
    setLoading: setLoading,
    loading: loading,
    sendEventToServer: sendEventToServer,
    startTime: startTime,
    setStartTime: setStartTime,
    endTime: endTime,
    setEndTime: setEndTime,
    setTimeZone: setTimeZone,
    timeZone: getValues('TimeZoneNameID') ?? getDefaultTimezone(),
    reminders: eventReminders,
    setReminders: setRemindersToEvent,
    RSVP: RSVP,
    setRSVP: setRSVP,
    notes: notes,
    setNotes: setNotes,
    setRecurrence: setRecurrence,
    setOpenDiscard: setOpenDiscard,
    openDiscard: openDiscard,
    setOpenAddReminder: setOpenAddReminder,
    openAddReminder: openAddReminder,
    setAttendees,
    attendeeText: attendeeText,
    setAttendeeText: setAttendeeText,
    addAttendee: addAttendeeToArray,
    addNote: addNoteToArray,
    participantsStatus: participantsStatus,
    setParticipantStatus,
    setStatus,
    register,
    isDirty,
    handleSubmit,
    getValues,
    control,
    setLocation,
    error,
    setError,
    setGroupID,
    attendees,
    setAllDay,
    cacheEventData,
    setName,
    setThirdPartyId,
    thirdPartyId: getValues('ThirdPartyID'),
    getCalendarMoveRequest: () => calendarMoveRequest,
    labels,
    setLabels,
    type: getValues('Type') ?? (is24meApp() ? DocType.TASK : DocType.EVENT),
    setType,
    updateNote,
    someday,
    setSomeday,
    createAtStart,
    createAtEnd,
    priority: getValues('Priority') ?? '0',
    setPriority: setPriority,
    titleSet,
    onTitleSet,
    noteText: noteText,
    setNoteText,
    isNewEvent: newEvent,
    ownerId,
    setOwnerId,
    groupId: getValues('GroupID'),
    dirtyFields,
    locationPendingText: getValues('locationPendingText'),
    setLocationPendingText,
    setNotePendingText,
    setNotePendingBytes,
    notePendingText: getValues('notePendingText'),
    notePendingBytes: getValues('notePendingBytes'),
    addLocationToEvent,
    applyPendingData,
    supplementaryGroupIds,
    setSupplementaryGroupIds,
    attachements,
    setAttachments,
    customUrl,
    setCustomUrl,
    locationReminder: getValues('LocationReminders') ?? [],
    selectedColor,
    setSelectedColor,
    isLocalSaveOnly,
    taskType: taskType,
    setTaskType: setTaskType
  }

  return eventPopupInterface
}

function arraysAreEqual(a: any[], b: any[]): boolean {
  return isEqual(a, b)
}
